import { FunctionComponent } from "react";

import {
  FontIcon,
  getTheme,
  mergeStyleSets,
  Option,
  OptionsSelect,
  OptionsSelectProps,
  Stack
} from "@bps/fluent-ui";
import { useStores } from "@stores/hooks/useStores.ts";
import { PracOrgUnit } from "@stores/practice/models/PracOrgUnit.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { LocationOption } from "@ui-components/selects/LocationOption.tsx";

export interface LocationsSelectProps
  extends Omit<OptionsSelectProps, "options"> {
  onFilter?: (options: Option<PracOrgUnit>[]) => Option<PracOrgUnit>[];
  showIcon?: boolean;
  showInactiveLocation?: boolean;
  disableInactiveLocation?: boolean;
  showDefaultLocationBadge?: boolean;
  isBhbSettingsScreen?: boolean;
}

const CALLOUT_WIDTH_WITH_INACTIVE_LOCATION = 435;
const CALLOUT_WIDTH_ON_BHB_SCREEN = 180;
export const LocationsSelect: FunctionComponent<LocationsSelectProps> = ({
  onFilter,
  placeholder,
  showIcon,
  showInactiveLocation,
  disableInactiveLocation,
  showDefaultLocationBadge,
  isBhbSettingsScreen,
  ...rest
}) => {
  const theme = getTheme();
  const {
    core: { getLocationName, hasInactiveFlag }
  } = useStores();

  const sortLocationsByInactiveFlagAndName = (
    a: { isInactive: boolean | undefined; text: string },
    b: { isInactive: boolean | undefined; text: string }
  ) => {
    if (a.isInactive && !b.isInactive) return 1;
    if (!a.isInactive && b.isInactive) return -1;
    return a.text.localeCompare(b.text);
  };

  const getOptions = (locations: PracOrgUnit[]): Option<PracOrgUnit>[] => {
    const options = locations
      .map(location => ({
        key: location.id,
        text: getLocationName(location.id) ?? "",
        data: location,
        isInactive: hasInactiveFlag(location.id),
        disabled: disableInactiveLocation && hasInactiveFlag(location.id)
      }))
      .sort(sortLocationsByInactiveFlagAndName)
      .filter(location => {
        if (!showInactiveLocation) {
          return !location.isInactive;
        }
        return true;
      });

    return onFilter ? onFilter(options) : options;
  };

  const onRenderFieldContentSingle = (options: Option<PracOrgUnit>[]) => {
    if (options.length > 0) {
      const selectedOptions = options.map(opt => onRenderOption(opt));
      return (
        <Stack
          role="option"
          aria-label={options.map(opt => opt.text).join(" ")}
          wrap
          horizontal
          tokens={{
            childrenGap: 10
          }}
        >
          {selectedOptions}
        </Stack>
      );
    } else {
      return placeholder;
    }
  };

  const onRenderFieldContentMultiple = (options: Option<PracOrgUnit>[]) => {
    if (options.length > 0) {
      const label =
        options.length === 1 ? options[0].text : `${options.length} locations`;
      return (
        <Stack
          horizontal
          tokens={{ childrenGap: 8 }}
          verticalAlign="center"
          horizontalAlign="center"
          aria-label={label}
          role="option"
        >
          <FontIcon
            iconName="BpOrgItem"
            styles={{ root: { color: theme.palette.themePrimary } }}
          />
          <Stack.Item>{label}</Stack.Item>
        </Stack>
      );
    } else {
      return placeholder;
    }
  };

  const onRenderOption = (option: Option<PracOrgUnit>) => (
    <LocationOption
      key={option.key}
      color={option.data?.orgUnitLocationData?.appointmentBookMarkerCode}
      text={option.text}
      showIcon={showIcon}
      showInactiveBadge={hasInactiveFlag(option?.key)}
      showDefaultLocationBadge={
        showDefaultLocationBadge && option.data?.isDefault
      }
    />
  );

  const getCalloutWidth = () => {
    if (showInactiveLocation) {
      return CALLOUT_WIDTH_WITH_INACTIVE_LOCATION;
    } else if (isBhbSettingsScreen) {
      return CALLOUT_WIDTH_ON_BHB_SCREEN;
    }
    return undefined;
  };
  return (
    <DataFetcher
      fetch={({ practice }) => practice.getAllOrgUnitsLocationData()}
    >
      {locations => {
        const options: Option<PracOrgUnit>[] = getOptions(locations);
        return (
          <OptionsSelect
            {...rest}
            calloutWidth={getCalloutWidth()}
            styles={mergeStyleSets(
              {
                root: {
                  width: isBhbSettingsScreen
                    ? CALLOUT_WIDTH_ON_BHB_SCREEN
                    : undefined
                }
              },
              rest.styles
            )}
            options={options}
            onRenderOption={onRenderOption}
            onRenderFieldContent={
              rest.multiSelect
                ? onRenderFieldContentMultiple
                : onRenderFieldContentSingle
            }
          />
        );
      }}
    </DataFetcher>
  );
};
